import { useState } from "react";
import WavesurferPlayer from "@wavesurfer/react";
import { FileData } from "../types/api";
import { PlayIcon, PauseIcon } from "@heroicons/react/20/solid";
import { getHexColorValue } from "../theme";
import WaveSurfer from "wavesurfer.js";
import { getFirstDownloadUrl } from "./downloadUrlCache";

// Helper function to format time in MM:SS format
const formatTime = (seconds: number) => {
  if (isNaN(seconds)) return "0:00";

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const AudioFileMessageItemEntry = ({ file }: { file: FileData }) => {
  const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [playbackRate, setPlaybackRate] = useState<1 | 1.5 | 2>(1);

  // Handle play/pause toggle
  const togglePlay = () => {
    if (wavesurfer) {
      wavesurfer.playPause();
    }
  };

  // Playback rate cycling
  const cyclePlaybackRate = () => {
    if (!wavesurfer) return;

    const rates = [1, 1.5, 2] as const;
    const currentIndex = rates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % rates.length;
    const nextRate = rates[nextIndex];

    wavesurfer.setPlaybackRate(nextRate);
    setPlaybackRate(nextRate);
  };

  // Handle wavesurfer ready event
  const onReady = (ws: WaveSurfer) => {
    setWavesurfer(ws);
    setDuration(ws.getDuration());
  };

  // Handle time update without throttling
  const onAudioprocess = (ws: WaveSurfer) => {
    setCurrentTime(ws.getCurrentTime());
  };

  return (
    <div className="relative rounded bg-slate-100 flex items-center w-full overflow-hidden border border-slate-200">
      <div className="flex w-full p-2">
        <div className="flex items-center gap-2 w-full ">
          {/* 1. Play/Pause button */}
          <button
            onClick={togglePlay}
            className="text-gray-600 hover:text-gray-700 transition-colors flex-shrink-0 p-4 w-12"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <PauseIcon className="h-5 w-5" />
            ) : (
              <PlayIcon className="h-5 w-5" />
            )}
          </button>

          {/* 2. Waveform visualization */}
          <div className="flex-1 h-10 w-40 cursor-pointer">
            <WavesurferPlayer
              height={40}
              waveColor={getHexColorValue("brand")}
              progressColor={getHexColorValue("brand-hover")}
              cursorColor="#9ca3af"
              url={getFirstDownloadUrl(file.download_url)}
              barWidth={3}
              barGap={1}
              barRadius={3}
              normalize={true}
              onReady={onReady}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onFinish={() => setIsPlaying(false)}
              onAudioprocess={onAudioprocess}
              onSeeking={() => wavesurfer?.isPlaying() || wavesurfer?.play()}
            />
          </div>

          {/* 3. Time display OR playback rate based on play state */}
          <div className="text-center w-12 font-semibold">
            {isPlaying ? (
              <button
                onClick={cyclePlaybackRate}
                className="w-12 text-sm text-gray-600 cursor-pointer p-1 hover:bg-gray-300 hover:rounded-full"
                aria-label="Change playback speed"
              >
                {playbackRate}x
              </button>
            ) : (
              <span className="w-12 text-sm text-gray-600 ">
                {/* {formatTime(currentTime)} /  */}
                {formatTime(duration)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioFileMessageItemEntry;
